import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { unwrapResponse } from '../utils/unwrapResponse';
import { queryKeys } from '../hooks/queries/queryKeys';
import { TOS_REQUIRED_EVENT } from '../utils/tosEvents';

const REQUIRED_TOS_VERSION = '1.3';

interface ToSStatus {
  currentVersion: string;
  currentVersionEffectiveDate: string;
  userAcceptedVersion: string | null;
  userAcceptedAt: string | null;
  needsAcceptance: boolean;
}

const ToSReacceptanceModal: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const acceptButtonRef = useRef<HTMLButtonElement>(null);

  // CRITICAL (blocks all app usage when needsAcceptance is true): on error,
  // query.data stays undefined -- isVisible below requires truthy data, so
  // a failed fetch fails OPEN (no blocking modal), exactly matching the
  // pre-migration behavior (catch swallowed the error, tosStatus stayed
  // null, isVisible stayed false). Never let this hang or block: retry:false
  // (pre-migration had no retry wrapper either) means a failure resolves to
  // isError quickly rather than spinning through the global 2-retry backoff.
  const tosStatusQuery = useQuery<ToSStatus>({
    queryKey: queryKeys.auth.tosStatus(user?.id ?? ''),
    queryFn: async () => {
      const r = await authAPI.getTosStatus();
      return unwrapResponse<ToSStatus>(r.data);
    },
    enabled: !!user,
    retry: false,
  });

  // Pre-migration console.error'd a failed status check; preserved here
  // since useQuery (v5) has no onError callback.
  useEffect(() => {
    if (tosStatusQuery.error) {
      console.error('Failed to check ToS status:', tosStatusQuery.error);
    }
  }, [tosStatusQuery.error]);

  // Spec 327: the server can start answering 403 TOS_ACCEPTANCE_REQUIRED
  // between this query's refetches; the API client fires TOS_REQUIRED_EVENT so
  // we re-check at once instead of leaving the user facing bare 403s.
  // A page firing N parallel requests gets N 403s; re-check at most once per
  // 5s so a burst (or a polling query) can't hammer the status endpoint.
  useEffect(() => {
    let lastRecheck = 0;
    const recheck = () => {
      const now = Date.now();
      if (now - lastRecheck < 5000) return;
      lastRecheck = now;
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.tosStatus(user?.id ?? '') });
    };
    window.addEventListener(TOS_REQUIRED_EVENT, recheck);
    return () => window.removeEventListener(TOS_REQUIRED_EVENT, recheck);
  }, [queryClient, user?.id]);

  const tosStatus = tosStatusQuery.data;
  const isVisible = !!user && !!tosStatus && tosStatus.needsAcceptance;

  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => acceptButtonRef.current?.focus(), 100);
    return () => { clearTimeout(timer); };
  }, [isVisible]);

  const acceptMutation = useMutation({
    mutationFn: () => authAPI.acceptTos(),
    // Awaited by handleAccept below via mutateAsync -- invalidate returns a
    // promise that resolves once the active tosStatus query has refetched,
    // so the button's loading state covers both calls exactly like the
    // pre-migration accept-then-refetch sequence did.
    // Spec 327: under server-side enforcement every non-exempt query (orgs,
    // features, the page's own data) may be sitting in a 403 error state from
    // before acceptance. Refetch everything active, not just the ToS status,
    // or the app stays broken until a hard reload. Includes tosStatus.
    onSuccess: () => queryClient.invalidateQueries(),
    retry: false,
  });

  const handleAccept = async () => {
    setError('');
    try {
      await acceptMutation.mutateAsync();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr?.response?.data?.error || (err instanceof Error ? err.message : '') || 'Failed to accept Terms of Service');
    }
  };

  const loading = acceptMutation.isPending;

  if (!isVisible || !tosStatus) return null;

  return (
    <Modal
      isOpen={isVisible}
      onClose={() => {/* Blocking modal — cannot be dismissed */}}
      title="We've Updated Our Terms"
      size="md"
      variant="centered"
      footer={
        <div className="w-full">
          <Button ref={acceptButtonRef} variant="primary" fullWidth onClick={handleAccept} disabled={loading} size="lg">
            {loading ? 'Accepting...' : 'I Accept'}
          </Button>
          <p className="mt-3 text-xs text-text-tertiary text-center leading-relaxed">
            By clicking &ldquo;I Accept&rdquo;, you agree to our updated{' '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-accent no-underline hover:underline">Terms of Service</a>{' '}and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-accent no-underline hover:underline">Privacy Policy</a>.
          </p>
        </div>
      }
    >
      {tosStatus.currentVersion && <span className="text-xs font-semibold text-accent">Version {REQUIRED_TOS_VERSION}</span>}

      <h3 className="text-base font-semibold text-text-primary m-0 mb-3 mt-3">What's Changed</h3>
      <ul className="m-0 pl-5 flex flex-col gap-2.5 text-sm text-text-secondary leading-relaxed mb-4">
        <li><strong className="text-text-primary">Mentorship Program:</strong> We added terms covering our new mentorship feature, including how mentors and mentees interact, content sharing between mentors, and what happens when mentorship ends.</li>
        <li><strong className="text-text-primary">Content Sharing:</strong> New rules about sharing your content (protocols, journal entries, limits) to community channels and with mentors. Your limits are always anonymized and scene partner names are always redacted.</li>
        <li><strong className="text-text-primary">Community Channels:</strong> Updated how community content works with different visibility levels (public, restricted, and private hierarchy channels). All channels are moderated for safety.</li>
        <li><strong className="text-text-primary">Identity Selection:</strong> Your role identity (selected during registration) is used to personalize your experience. You can change or remove it anytime.</li>
        <li><strong className="text-text-primary">Updated Contact Info:</strong> Our domain is now helmapp.co.</li>
      </ul>
      <div className="mb-4">
        <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-accent font-medium no-underline hover:underline text-sm">
          Read the full Terms of Service
        </a>
      </div>
      {error && <div className="bg-danger/10 border border-danger/20 text-danger p-3 rounded-lg text-sm" role="alert">{error}</div>}
    </Modal>
  );
};

export default ToSReacceptanceModal;
